/**
 * "Open this in the app" for the six banking apps that matter most for PayNow in Singapore. There
 * is no PayNow/SGQR deep-link standard, so this can only bring the app to the front, as tapping its
 * icon would, not pass the QR in; the payer then uses the app's own "Scan & Pay".
 *
 * Both platforms need the app's own registered URL scheme (a store ID or package name alone falls
 * through to the store). iOS and Android schemes differ per app, so they are tracked separately;
 * Android's come from `adb shell dumpsys package <pkg>`, iOS's from Info.plist (`CFBundleURLTypes`).
 * An Android intent-filter with an Authority only matches that exact host, and one without only
 * matches a host-less URI. DBS and POSB digibank have no Android scheme (four live attempts each
 * never opened the app), so they go to the Play Store.
 */
export interface BankApp {
  id: string;
  name: string;
  logo: string;
  /** Custom URL scheme for iOS (hidden-iframe attempt), no "://". */
  iosScheme: string;
  /** True once `iosScheme` is confirmed on a real device or in the app's Info.plist. */
  iosConfirmed?: boolean;
  /**
   * Host to send alongside `iosScheme` (e.g. "readQR" -> "scheme://readQR"). Undefined sends the
   * bare scheme, which every confirmed iOS scheme accepts.
   */
  iosHost?: string;
  /**
   * Custom URL scheme for Android (intent:// scheme= param), no "://". Omitted when no safe scheme
   * is known; `openBankApp` then goes straight to the Play Store.
   */
  androidScheme?: string;
  /** True once `androidScheme` is confirmed via adb dumpsys or a real device. */
  androidConfirmed?: boolean;
  /**
   * Authority (host) the app's intent-filter requires alongside the scheme; the URI's host must
   * match it exactly.
   */
  androidHost?: string;
  iosAppStoreUrl: string;
  androidPackage: string;
  androidPlayStoreUrl: string;
}

export const BANK_APPS: BankApp[] = [
  {
    id: "dbs-digibank",
    // DBS digibank and POSB digibank are separate apps; don't collapse them into one "DBS/POSB" chip.
    name: "DBS digibank",
    logo: new URL("../assets/dbs-digibank-icon.jpg", import.meta.url).href,
    iosScheme: "dbsdigibank",
    iosConfirmed: true,
    // No Android scheme: four live attempts (no host, a placeholder, its declared Authority, its
    // package name as host) never opened the app, so it goes to the Play Store.
    iosAppStoreUrl: "https://apps.apple.com/sg/app/dbs-digibank/id1068403826",
    androidPackage: "com.dbs.sg.dbsmbanking",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.dbs.sg.dbsmbanking",
  },
  {
    id: "posb-digibank",
    name: "POSB digibank",
    logo: new URL("../assets/posb-digibank-icon.jpg", import.meta.url).href,
    // The iOS scheme is "posbdigibanksg", not "posbdigibank". No Android scheme, as for DBS digibank.
    iosScheme: "posbdigibanksg",
    iosConfirmed: true,
    iosAppStoreUrl: "https://apps.apple.com/sg/app/posb-digibank/id1068416633",
    androidPackage: "com.dbs.sg.posbmbanking",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.dbs.sg.posbmbanking",
  },
  {
    id: "dbs-paylah",
    name: "DBS PayLah!",
    logo: new URL("../assets/dbs-paylah-icon.jpg", import.meta.url).href,
    // Android scheme from `dumpsys package com.dbs.dbspaylah`; the iOS scheme from the app's Info.plist.
    iosScheme: "dbspaylah",
    iosConfirmed: true,
    androidScheme: "dbspaylah",
    androidConfirmed: true,
    // dumpsys lists three authorities (www.myinfo.com, com.dbs.paylah, com.dbs.digisg); which one is
    // right is unconfirmed. Test them via /scheme-tester.
    androidHost: "com.dbs.paylah",
    iosAppStoreUrl: "https://apps.apple.com/sg/app/dbs-paylah/id878528688",
    androidPackage: "com.dbs.dbspaylah",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.dbs.dbspaylah",
  },
  {
    id: "ocbc-digital",
    name: "OCBC Digital",
    logo: new URL("../assets/ocbc-icon.jpg", import.meta.url).href,
    // iOS uses "ocbc", not Android's "ocbcmb": bare `ocbcmb://` hangs after login and
    // `ocbcmb://readQR` lands in a merchant-checkout error.
    iosScheme: "ocbc",
    iosConfirmed: true,
    androidScheme: "ocbcmb",
    androidHost: "readQR",
    androidConfirmed: true,
    iosAppStoreUrl: "https://apps.apple.com/sg/app/ocbc-singapore/id292506828",
    androidPackage: "com.ocbc.mobile",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.ocbc.mobile",
  },
  {
    id: "uob-tmrw",
    name: "UOB TMRW",
    logo: new URL("../assets/uob-tmrw-icon.jpg", import.meta.url).href,
    // iOS registers "mightysgapp", not "app": "app" fell through to the App Store on a real iPhone.
    iosScheme: "mightysgapp",
    iosConfirmed: true,
    androidScheme: "app",
    androidHost: "mightysg",
    androidConfirmed: true,
    iosAppStoreUrl: "https://apps.apple.com/sg/app/uob-tmrw/id1049286296",
    androidPackage: "com.uob.mighty.app",
    androidPlayStoreUrl:
      "https://play.google.com/store/apps/details?id=com.uob.mighty.app",
  },
  {
    id: "trust-bank",
    name: "Trust Bank SG",
    logo: new URL("../assets/trust-bank-icon.jpg", import.meta.url).href,
    // An empty host crashed the app once on Android while a placeholder host opened it; tested
    // successfully only once, so watch for regressions.
    iosScheme: "trust",
    iosConfirmed: true,
    androidScheme: "trust",
    iosAppStoreUrl: "https://apps.apple.com/sg/app/trust-bank-sg/id1598460384",
    androidPackage: "sg.trust",
    androidPlayStoreUrl: "https://play.google.com/store/apps/details?id=sg.trust",
  },
];

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

function isIOS(): boolean {
  // iPadOS 13+ reports as "MacIntel" with touch support — catch that too.
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** Best-effort "bring this app to the front, or send the payer to install it." */
export function openBankApp(app: BankApp): void {
  if (isAndroid()) {
    if (!app.androidScheme) {
      // No usable scheme (DBS and POSB digibank): go straight to the Play Store.
      window.location.href = app.androidPlayStoreUrl;
      return;
    }
    // `scheme=` is required; `package=` alone doesn't resolve. Chrome strips S.browser_fallback_url
    // before handing off, so the fallback fires only when nothing resolves. An empty host can make
    // Chrome drop the whole intent:// with no fallback, so use a placeholder if androidHost is unknown.
    const fallback = encodeURIComponent(app.androidPlayStoreUrl);
    const host = app.androidHost ?? "_";
    window.location.href =
      `intent://${host}#Intent;scheme=${app.androidScheme};package=${app.androidPackage};` +
      `S.browser_fallback_url=${fallback};end`;
    return;
  }

  if (isIOS()) {
    let handled = false;
    const finish = () => {
      if (handled) return;
      handled = true;
      document.removeEventListener("visibilitychange", onHide);
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") finish();
    };
    document.addEventListener("visibilitychange", onHide);

    // A hidden iframe avoids Safari's "Cannot Open Page" alert for a wrong scheme guess.
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `${app.iosScheme}://${app.iosHost ?? ""}`;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), 2000);

    setTimeout(() => {
      if (!handled && document.visibilityState === "visible") {
        finish();
        window.location.href = app.iosAppStoreUrl;
      }
    }, 1200);
    return;
  }

  // Desktop or anything else: nothing to open, so send them to the listing.
  window.open(app.androidPlayStoreUrl, "_blank", "noopener");
}
